import { apiClient } from '@/api/client';
import type { CreateCaseRequest, CaseResponse, GetCasesResponse, GetCasesParams, CaseDetailResponse, GetRecommendedCasesParams, GetRecommendedCasesResponse, GetParentCasesParams, GetParentCasesResponse } from '@/types/api';

/**
 * POST /api/cases
 * Create a new tuition case (job posting)
 * Requires JWT authentication and parent profile
 */
export const createCase = async (
  request: CreateCaseRequest
): Promise<CaseResponse['data']> => {
  const response = await apiClient.post<CaseResponse>('/cases', request);

  if (!response.data.success) {
    throw new Error('Failed to create tuition case');
  }

  return response.data.data;
};

/**
 * GET /api/cases/recommended
 * Get recommended cases for carousel display
 * Returns the newest active cases with pagination support
 * Public endpoint - no authentication required (optional auth for preferences)
 */
export const getRecommendedCases = async (
  params?: GetRecommendedCasesParams
): Promise<GetRecommendedCasesResponse['data']> => {
  const response = await apiClient.get<GetRecommendedCasesResponse>('/cases/recommended', { params });

  if (!response.data.success) {
    throw new Error('Failed to fetch recommended cases');
  }

  return response.data.data;
};

/**
 * GET /api/cases
 * Get all active tuition cases with filters and pagination
 * Public endpoint - no authentication required
 */
export const getCases = async (
  params?: GetCasesParams
): Promise<GetCasesResponse['data']> => {
  const response = await apiClient.get<GetCasesResponse>('/cases', { params });

  if (!response.data.success) {
    throw new Error('Failed to fetch tuition cases');
  }

  return response.data.data;
};

/**
 * GET /api/cases/:id
 * Get a specific tuition case with parent profile details
 * Public endpoint - no authentication required
 */
export const getCaseById = async (id: string, source?: string): Promise<CaseDetailResponse['data']> => {
  const response = await apiClient.get<CaseDetailResponse>(`/cases/${id}`, {
    params: source ? { source } : undefined,
  });

  if (!response.data.success) {
    throw new Error('Failed to fetch case details');
  }

  return response.data.data;
};

/**
 * PATCH /api/cases/:id
 * Update an existing tuition case
 * Requires JWT authentication and must be case owner
 */
export const updateCase = async (
  id: string,
  request: CreateCaseRequest
): Promise<CaseResponse['data']> => {
  const response = await apiClient.patch<CaseResponse>(`/cases/${id}`, request);

  if (!response.data.success) {
    throw new Error('Failed to update tuition case');
  }

  return response.data.data;
};

/**
 * GET /api/cases/my-cases
 * Get all open tuition cases belonging to the authenticated parent
 * Requires JWT authentication and parent role
 */
export const getParentCases = async (
  params?: GetParentCasesParams
): Promise<GetParentCasesResponse['data']> => {
  const response = await apiClient.get<GetParentCasesResponse>('/cases/my-cases', { params });

  if (!response.data.success) {
    throw new Error('Failed to fetch parent cases');
  }

  return response.data.data;
};

/**
 * DELETE /api/cases/:id
 * Delete a tuition case
 * Requires JWT authentication and must be case owner
 */
export const deleteCase = async (id: string): Promise<void> => {
  const response = await apiClient.delete<{ success: true }>(`/cases/${id}`);

  if (!response.data.success) {
    throw new Error('Failed to delete tuition case');
  }
};
